import { toLocalIsoDate, todayLocalIso } from './localDate';

export type DateRangePreset =
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'last_30_days'
  | 'this_quarter'
  | 'last_quarter'
  | 'last_3_months'
  | 'last_6_months'
  | 'last_12_months'
  | 'this_year'
  | 'last_year'
  | 'all'
  | 'custom';

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface DateRangePresetOption {
  value: DateRangePreset;
  label: string;
}

export const DATE_RANGE_PRESET_OPTIONS: DateRangePresetOption[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This week' },
  { value: 'last_week', label: 'Last week' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'this_quarter', label: 'This quarter' },
  { value: 'last_quarter', label: 'Last quarter' },
  { value: 'last_3_months', label: 'Last 3 months' },
  { value: 'last_6_months', label: 'Last 6 months' },
  { value: 'last_12_months', label: 'Last 12 months' },
  { value: 'this_year', label: 'This year' },
  { value: 'last_year', label: 'Last year' },
  { value: 'all', label: 'All dates' },
  { value: 'custom', label: 'Custom range' },
];

export const DEFAULT_DATE_RANGE_PRESET: DateRangePreset = 'all';

export const toIsoDate = toLocalIsoDate;

export const todayIso = todayLocalIso;

const startOfWeek = (date: Date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  return d;
};

const endOfWeek = (date: Date) => {
  const d = startOfWeek(date);
  d.setDate(d.getDate() + 6);
  return d;
};

const startOfMonth = (year: number, month: number) => new Date(year, month, 1);

const endOfMonth = (year: number, month: number) => new Date(year, month + 1, 0);

const startOfQuarter = (date: Date) => {
  const quarterStartMonth = Math.floor(date.getMonth() / 3) * 3;
  return new Date(date.getFullYear(), quarterStartMonth, 1);
};

const endOfQuarter = (date: Date) => {
  const start = startOfQuarter(date);
  return endOfMonth(start.getFullYear(), start.getMonth() + 2);
};

export const getDateRangeForPreset = (preset: DateRangePreset): DateRange => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();

  switch (preset) {
    case 'today':
      return { startDate: toIsoDate(today), endDate: toIsoDate(today) };
    case 'yesterday': {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return { startDate: toIsoDate(yesterday), endDate: toIsoDate(yesterday) };
    }
    case 'this_week':
      return { startDate: toIsoDate(startOfWeek(today)), endDate: toIsoDate(today) };
    case 'last_week': {
      const lastWeekEnd = new Date(startOfWeek(today));
      lastWeekEnd.setDate(lastWeekEnd.getDate() - 1);
      const lastWeekStart = startOfWeek(lastWeekEnd);
      return {
        startDate: toIsoDate(lastWeekStart),
        endDate: toIsoDate(endOfWeek(lastWeekEnd)),
      };
    }
    case 'this_month':
      return { startDate: toIsoDate(startOfMonth(year, month)), endDate: toIsoDate(today) };
    case 'last_month': {
      const lastMonth = month === 0 ? 11 : month - 1;
      const lastMonthYear = month === 0 ? year - 1 : year;
      return {
        startDate: toIsoDate(startOfMonth(lastMonthYear, lastMonth)),
        endDate: toIsoDate(endOfMonth(lastMonthYear, lastMonth)),
      };
    }
    case 'last_30_days': {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      return { startDate: toIsoDate(start), endDate: toIsoDate(today) };
    }
    case 'this_quarter':
      return { startDate: toIsoDate(startOfQuarter(today)), endDate: toIsoDate(today) };
    case 'last_quarter': {
      const currentQuarterStart = startOfQuarter(today);
      const lastQuarterEnd = new Date(currentQuarterStart);
      lastQuarterEnd.setDate(lastQuarterEnd.getDate() - 1);
      return {
        startDate: toIsoDate(startOfQuarter(lastQuarterEnd)),
        endDate: toIsoDate(endOfQuarter(lastQuarterEnd)),
      };
    }
    case 'last_3_months':
      return {
        startDate: toIsoDate(startOfMonth(year, month - 2)),
        endDate: toIsoDate(today),
      };
    case 'last_6_months':
      return {
        startDate: toIsoDate(startOfMonth(year, month - 5)),
        endDate: toIsoDate(today),
      };
    case 'last_12_months':
      return {
        startDate: toIsoDate(startOfMonth(year, month - 11)),
        endDate: toIsoDate(today),
      };
    case 'this_year':
      return { startDate: toIsoDate(new Date(year, 0, 1)), endDate: toIsoDate(today) };
    case 'last_year':
      return {
        startDate: toIsoDate(new Date(year - 1, 0, 1)),
        endDate: toIsoDate(new Date(year - 1, 11, 31)),
      };
    case 'all':
      return { startDate: '', endDate: '' };
    case 'custom':
    default:
      return { startDate: '', endDate: '' };
  }
};

export const getDateRangePresetLabel = (
  preset: DateRangePreset,
  startDate: string,
  endDate: string
): string => {
  if (preset === 'custom') {
    if (startDate && endDate) {
      return `${startDate} – ${endDate}`;
    }
    return 'Custom range';
  }

  return DATE_RANGE_PRESET_OPTIONS.find((option) => option.value === preset)?.label ?? 'Date';
};
